import { Injectable } from '@angular/core';
import { AppKey } from '@galaxy/marketplace-apps';
import { Account, AccountsService } from '@vendasta/accounts/legacy';
import { ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as AppsAndAddonsActivationStatusesInterface } from '@vendasta/accounts/legacy/_internal/interfaces/api.interface';
import { ProductService as MarketplaceProductService } from '@vendasta/marketplace-packages';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, skipWhile, switchMap } from 'rxjs/operators';
import { environment, partnerId } from '../../globals';
import { AccountGroup, AccountGroupService } from '../account-group/';
import { AppConfigService } from '../app-config.service';
import { DIRECT_WEST_VISIBLE_PRODUCTS, isGSuiteTransfer } from '../product/product-access-constants';
import { AppPartnerService } from './app_partner.service';
import { EDITION, Product } from './product';

dayjs.extend(utc);

export const EXPRESS_EDITION_IDS = Object.freeze(['EDITION-38SMW45H', 'EDITION-F7JZ5TV8']);
export const PRO_EDITION_IDS = Object.freeze(['EDITION-FGVLLVMN', 'EDITION-GGNG62B5']);

@Injectable({ providedIn: 'root' })
export class ProductService {
  readonly expiredTrialAccounts$ = this.accountGroupService.currentAccountGroup$.pipe(
    switchMap((ag) => this.accountsService.list(ag.accountGroupId, partnerId)),
    map((res) => res.accounts || []),
    map((accounts) => accounts.filter((a) => a.trial && a.deactivation)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly platformApps$ = this.appConfigService.businessAppName$.pipe(
    map((name) => [
      new Product({
        productId: 'VBC',
        name: name,
        iconUrl: `https://vbc-${environment}.appspot.com/static/images/file-upload.png`,
        origin: 'partner_created',
      }),
    ]),
  );

  // DEPRECATED. DO NOT USE.
  // Using this results in a very large/slow list products API call.
  readonly marketplaceProducts$ = combineLatest([
    this.accountGroupService.currentAccountGroup$,
    this.accountGroupService.currentAccountGroup$.pipe(
      switchMap((ag) => this.marketplaceProductService.loadAllProducts(partnerId, 500, '', ag.marketId)),
      map((resp) => resp.products.map((product) => new Product(product))),
    ),
    this.accountGroupService.currentAccountGroup$.pipe(
      switchMap((ag) => this.appPartnerService.listAppSettings(partnerId, ag.marketId, '', 500)),
    ),
  ]).pipe(
    map(([ag, allProducts, allAppSettings]) => {
      return allProducts.map((product) => {
        // Update EntryUrl
        if (product.entryUrl) {
          product.entryUrl = product.entryUrl.replace(new RegExp('<accountId>', 'g'), ag.accountGroupId);
        }
        // Update Branding
        for (const appSetting of allAppSettings) {
          if (appSetting.appId === product.productId) {
            if (appSetting.branding && appSetting.branding.enabled) {
              if (appSetting.branding.name) {
                product.name = appSetting.branding.name;
              }
              if (appSetting.branding.iconUrl) {
                product.icon = appSetting.branding.iconUrl;
                product.iconUrl = appSetting.branding.iconUrl;
              }
            }
            break;
          }
        }
        return product;
      });
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly activeProducts$ = this.accountGroupService.currentAccountGroup$.pipe(
    switchMap((ag) => this.loadActiveProducts(ag.accountGroupId, ag.marketId)),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  readonly execReportActiveProducts$ = combineLatest([
    this.appConfigService.legacyConfig$,
    this.accountGroupService.currentAccountGroup$,
  ]).pipe(
    switchMap(([config, ag]) => {
      if (config.decoupleProductAndReportAccess) {
        return this.accountsService
          .listAppsAndAddonsActivationStatusesForBusiness(ag.accountGroupId, {
            // 1 is the status for active accounts, so this filter only returns active, not trial accounts
            statuses: [1],
          })
          .pipe(
            map((res: AppsAndAddonsActivationStatusesInterface[]) => {
              if (!res) {
                return [];
              }
              return res.map(
                (activation) =>
                  new AppKey({
                    appId: activation.appId,
                    editionId: activation.editionId,
                  }),
              );
            }),
            switchMap((keys) =>
              keys && keys.length > 0 ? this.appPartnerService.getMulti(keys, ag.marketId) : of([]),
            ),
            map((apps) => apps.map(Product.fromV2App).filter((product) => product.hiddenFromDashboard !== true)),
          );
      }
      return this.activeProducts$;
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  readonly activeAccessibleProducts$ = combineLatest([
    this.accountGroupService.currentAccountGroup$,
    this.activeProducts$,
  ]).pipe(
    map(([accountGroup, products]): Product[] => {
      if (!products) {
        return null;
      }
      return products.filter(
        (product) =>
          // User must have access to the product
          this.hasAccessToProduct(product.productId, accountGroup) &&
          // DirectWest has a specific list of products to display
          !shouldHideProductForDW(product, partnerId),
      );
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  constructor(
    private accountGroupService: AccountGroupService,
    private accountsService: AccountsService,
    private marketplaceProductService: MarketplaceProductService,
    private appPartnerService: AppPartnerService,
    private appConfigService: AppConfigService,
  ) {}

  static modifyProductsBasedOnActivations(
    products: Product[],
    accounts: Account[],
    trialAccounts: Account[],
    accountGroupId: string,
  ): Product[] {
    return (products || []).map((p) => {
      let customEntryUrl;

      // Set the edition
      p.edition = EDITION.STANDARD;

      if (p && p.productId && accounts && accounts.length > 0) {
        const accountForProduct = accounts.find((a) => p.isAccountOfProduct(a));
        if (accountForProduct) {
          customEntryUrl = accountForProduct.customEntryUrl;

          // Check the edition of the product
          if (accountForProduct.editionId) {
            if (EXPRESS_EDITION_IDS.indexOf(accountForProduct.editionId) > -1) {
              p.edition = EDITION.EXPRESS;
            } else if (PRO_EDITION_IDS.indexOf(accountForProduct.editionId) > -1) {
              p.edition = EDITION.PRO;
            }
          }
        }
      }

      // Prioritize the activation specific entry url over the sso urls
      if (customEntryUrl) {
        p.entryUrl = customEntryUrl;
      } else {
        p.renderBusinessIdIntoEntryUrl(accountGroupId);
      }

      const trialAccount = (trialAccounts || []).find((a) => p.isAccountOfProduct(a));
      if (trialAccount) {
        p.isTrial = true;
        p.trialDaysRemaining = ProductService.getTrialDaysRemaining(trialAccount, p);
      }

      p.active = true;
      return p;
    });
  }

  static getTrialDaysRemaining(trialAccount: Account, p: Product): number {
    let expiryDate = dayjs(trialAccount.activation).utc();
    expiryDate = expiryDate.add(p.trialDuration + 1, 'days');
    const currentDate = dayjs().utc();
    return Math.max(expiryDate.diff(currentDate, 'days'), 0);
  }

  loadActiveProducts(accountGroupId: string, marketId: string): Observable<Product[]> {
    return this.accountsService.list(accountGroupId, partnerId).pipe(
      map((res) => res.accounts || []),
      map((accounts) => accounts.filter((a) => !a.trial || (a.trial && dayjs(a.deactivation) > dayjs()))),
      switchMap((accounts) => {
        const trialAccounts = accounts.filter((a) => a.trial && dayjs(a.deactivation) > dayjs());

        const keys = accounts.map((account: Account) => {
          if (account.productId === 'CP') {
            return new AppKey({
              appId: account.accountId,
              editionId: account.editionId,
            });
          }

          return new AppKey({
            appId: account.appId || account.productId,
            editionId: account.editionId,
          });
        });

        return keys && keys.length > 0
          ? this.appPartnerService.getMulti(keys, marketId).pipe(
              map((apps) => apps.map(Product.fromV2App).filter((product) => product.hiddenFromDashboard !== true)),
              map((products) =>
                ProductService.modifyProductsBasedOnActivations(products, accounts, trialAccounts, accountGroupId),
              ),
            )
          : of([]);
      }),
    );
  }

  get activeProductsAndPlatformApps$(): Observable<Product[]> {
    return combineLatest([this.activeProducts$, this.platformApps$]).pipe(
      map(([products, platApps]) => {
        if (products == null || platApps == null) {
          return null;
        }
        return products.concat(platApps);
      }),
      skipWhile((products) => products === null),
    );
  }

  activeProductsAndPlatformAppsByAccountGroup$(accountGroupId: string, marketId: string): Observable<Product[]> {
    return combineLatest([this.loadActiveProducts(accountGroupId, marketId), this.platformApps$]).pipe(
      map(([products, platApps]) => {
        if (products == null || platApps == null) {
          return null;
        }
        return products.concat(platApps);
      }),
      skipWhile((products) => products === null),
    );
  }

  get newProductIds(): string[] {
    const cookieValue = document.cookie.match('newPurchases=([^;]*);?');
    let purchasedProductIds: string[];
    if (cookieValue && cookieValue.length > 1) {
      purchasedProductIds = cookieValue[1].split(',');
    }
    return purchasedProductIds;
  }

  private hasAccessToProduct(productId: string, accountGroup: AccountGroup): boolean {
    if (accountGroup.suspended && productId !== 'MS') {
      return false;
    }
    if (isGSuiteTransfer(productId)) {
      return false;
    }
    return true;
  }
}

export function shouldHideProductForDW(product: Product, partnerId: string): boolean {
  // TODO get rid of this when we can
  if (partnerId !== 'DW') {
    return false;
  }
  return !DIRECT_WEST_VISIBLE_PRODUCTS.some((id) => id === product.productId || id === product.name);
}
